import type {
	PhaseAndMilestoneListItemType,
	TaskSetItem,
} from '@/features/ProjectPhase';
import { getUniqueId } from '@/utils/misc';
import {
	type PhasesTaskSetsQuery,
	ProjectPhaseTypeEnum as PhaseType,
	TYPES,
	type ProjectPhaseInput,
	type TaskSetsInput,
} from '@/generated/graphql';
import type { MilestoneItem, PhaseItem } from '@/features/ProjectPhase/types';
import { isNumber } from 'fp-ts/number';

export const getMaxDaysForPhase = (phase: PhaseItem): number | undefined => {
	if (phase.taskSets.length === 0) {
		return undefined;
	}

	const maxDays = Math.max(
		...phase.taskSets.map((bundle) =>
			isNumber(bundle.maxDays) ? bundle.maxDays : -1
		)
	);

	return maxDays === -1 ? undefined : maxDays;
};

export const parseProjectPhases = (
	data: PhasesTaskSetsQuery['PhasesTaskSets']
): PhaseAndMilestoneListItemType[] => {
	return data
		.map((row) => {
			const uuid = getUniqueId();

			if (row.__typename === TYPES.ProjectPhase) {
				if (row.type === PhaseType.Phase) {
					const item: PhaseItem = {
						...row,
						type: row.type,
						startDate: row.startDate || '',
						endDate: row.endDate || '',
						startDays: `${row.startDays ?? ''}`,
						endDays: `${row.endDays ?? ''}`,
						taskSets: (row.taskSets || []).map((set) => {
							return {
								id: set.id,
								name: set.name,
								maxDays: set.maxDays,
								minDays: set.minDays,
								taskTemplatesCount: set.taskTemplatesCount,
								ordering: set.ordering,
								uuid: getUniqueId(),
							};
						}),
						endDateDisabled: false,
						uuid,
						quoteLine: row.quoteLine,
					};

					item.endDateDisabled =
						getMaxDaysForPhase(item) !== undefined;

					return item;
				}
				if (row.type === PhaseType.Milestone) {
					const item: MilestoneItem = {
						...row,
						type: row.type,
						endDate: row.endDate || '',
						endDays: `${row.endDays}`,
						uuid,
					};

					return item;
				}
			} else if (row.__typename === TYPES.TaskSet) {
				const item: TaskSetItem = {
					...row,
					uuid,
				};

				return item;
			}

			return undefined;
		})
		.filter(
			(item): item is PhaseAndMilestoneListItemType => item !== undefined
		)
		.sort((a, b) => a.ordering - b.ordering);
};

export const transformPhasesToInput = (
	phases: PhaseAndMilestoneListItemType[],
	canReOrderPhases: boolean
): { phases: ProjectPhaseInput[]; taskSets: TaskSetsInput[] } => {
	const transformedPhases: ProjectPhaseInput[] = [];
	const transformedTaskSets: TaskSetsInput[] = [];

	phases?.forEach((phase: PhaseAndMilestoneListItemType) => {
		if (phase.type) {
			let newPhase: ProjectPhaseInput = {
				id: phase.id,
				type: phase.type,
				order: canReOrderPhases ? null : phase.ordering,
				title: phase.title,
				endDate: phase.endDate,
				endDays: phase.endDays,
			};

			if (phase.type === PhaseType.Phase) {
				const taskSets =
					phase.taskSets?.length > 0
						? phase.taskSets?.map((taskSet: TaskSetItem) => ({
								id: taskSet.id,
								ordering: taskSet.ordering,
							}))
						: null;

				newPhase = {
					...newPhase,
					quoteLineId: phase.quoteLine?.id,
					taskSets,
					startDate: phase.startDate,
					startDays: phase.startDays,
				};
			}
			transformedPhases.push(newPhase);
		} else {
			const newTaskSet: TaskSetsInput = {
				id: phase.id,
				ordering: phase.ordering,
			};
			transformedTaskSets.push(newTaskSet);
		}
	});

	return {
		phases: transformedPhases,
		taskSets: transformedTaskSets,
	};
};
